import {
  ProjectsService,
  Task,
  UsersService,
  UserSummary,
  ActivityDetails,
  ActionDetails,
  CompletionCriterionDetails,
  UserClaims,
  UserSettings,
  UserSettingsUpdateRequest,
  ActivityUpdate,
  ActionUpdateRequest,
  CompletionCriterionUpdateRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { ItemStatus, TaskItemState } from '@/common';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import { entraGetTokenSilent } from '@/store/authStore/entraClient';
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { useProjectStore } from '@/store/projectStore';
import { useUserStore } from '@/store/userStore';
import { getTenant } from '@/utils/helpers/urlManipulation';
import { fetchProfilePictureBlobUrl } from '@/utils/helpers/userApiHelpers';

// Extended Task type with activity_deadline for child tasks (Action/CC)
// This field is managed locally and not part of the API response
export type TaskWithActivityDeadline = Task & {
  activity_deadline?: string;
};

export type ProfileStoreState = {
  profile: UserClaims;
  settings: UserSettings;
  openRequests: number;
  tasks: TaskWithActivityDeadline[];
  image: string;
};

const initProfile = (): UserClaims => {
  return {
    id: '',
    email: '',
    username: '',
    first_name: null,
    last_name: null,
    scopes: {
      project_reader: [],
      project_creator: [],
      template_reader: [],
      template_creator: [],
    },
    roles: [],
  };
};

const initSettings = (): UserSettings => {
  return {
    user_id: '',
    email_notifications: false,
    email_language: 'de',
    project_favorites: [],
    email_tasks_notifications: false,
    teams_notifications: false,
    teams_notifications_available: false,
    slack_notifications: false,
    slack_notifications_available: false,
    email_tasks_threshold: 0,
    has_profile_picture: false,
    profile_picture_is_custom: false,
  };
};

export const useProfileStore = defineStore('profileStore', {
  state: (): ProfileStoreState => ({
    profile: initProfile(),
    settings: initSettings(),
    openRequests: 0,
    tasks: [],
    image: '',
  }),

  getters: {
    isLoading(state) {
      return state.openRequests > 0;
    },

    sortedTasks(state) {
      return state.tasks.sort(
        (a, b) => Number(new Date(a.date)) - Number(new Date(b.date)),
      );
    },
  },

  actions: {
    writeProfile(userSummary: UserSummary) {
      Object.assign(this.profile, userSummary);
    },

    async loadProfile() {
      const user = await UsersService.getUserClaims();
      if (this.$state.profile) {
        Object.assign(this.$state.profile, user);
      } else {
        this.$state.profile = user;
      }

      return this.$state.profile;
    },

    async loadUserSettings(skipImageLoad = false) {
      const legacyKey = 'favoriteProjects';
      const legacyFavs: string[] = JSON.parse(
        localStorage.getItem(legacyKey) ?? '[]',
      );

      let settings = await UsersService.getUserSettings();

      if (
        (!settings.project_favorites ||
          settings.project_favorites.length === 0) &&
        legacyFavs.length
      ) {
        try {
          settings = await UsersService.updateUserSettings({
            project_favorites: legacyFavs,
          });
        } catch (e) {
          console.error('Could not migrate legacy favorites', e);
        }
      }

      if (localStorage.getItem(legacyKey)) {
        localStorage.removeItem(legacyKey);
      }

      Object.assign(this.$state.settings, settings);

      if (!skipImageLoad && !this.image) {
        await this.loadMyProfilePicture(settings.has_profile_picture);
      }

      return this.$state.settings;
    },

    async saveUserSettings(settings: UserSettingsUpdateRequest) {
      return UsersService.updateUserSettings(settings).then((newSettings) => {
        Object.assign(this.$state.settings, newSettings);

        return this.$state.settings;
      });
    },

    /** Whether the user's email is in a Slack workspace of their realm. */
    async loadSlackAvailability() {
      const { available } = await UsersService.getSlackAvailability();

      return available;
    },

    writeTask(
      taskId: string,
      result: {
        state?: TaskItemState;
        status: ItemStatus | null;
        date?: string;
      },
    ) {
      const taskIndex = this.tasks.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) {
        return;
      }

      const task = this.tasks[taskIndex];

      // Update the target task
      this.tasks[taskIndex] = {
        ...task,
        state: result.state,
        status: result.status,
        date: result.date,
      };

      // Activity -> done: cascade to all incomplete child tasks
      if (task.type === 'Activity' && result.state === 'done') {
        this.tasks.forEach((_task, index) => {
          if (_task.activity_id === taskId && _task.state !== 'done') {
            this.tasks[index] = {
              ..._task,
              state: result.state,
              status: null,
            };
          }
        });
      }

      // Action/CC transitioning to underway or done from planned: cascade parent Activity to underway
      if (
        ['Action', 'CC'].includes(task.type) &&
        ['underway', 'done'].includes(result.state as string) &&
        task.state === 'planned' &&
        task.activity_id
      ) {
        const activityIndex = this.tasks.findIndex(
          (_task) =>
            _task.type === 'Activity' &&
            _task.id === task.activity_id &&
            _task.state === 'planned',
        );

        if (activityIndex !== -1) {
          // Parent Activity goes to underway (not done, even if child is done)
          // and inherits the status from the child task
          this.tasks[activityIndex] = {
            ...this.tasks[activityIndex],
            state: 'underway',
            status: result.status,
          };
        }
      }
    },

    async loadTasks(state: 'inProgress' | 'done') {
      const includePredecessors = useFeatureFlagStore()
        .hasPredecessorStateColumn
        ? '1'
        : undefined;

      return ProjectsService.getTasks(state, includePredecessors).then(
        (tasks) => {
          const activityDeadlines = new Map<string, string>();
          tasks.forEach((task) => {
            if (task.type === 'Activity' && task.id && task.date) {
              activityDeadlines.set(task.id, task.date);
            }
          });

          this.tasks = tasks.map((task) => {
            if (
              ['Action', 'CC'].includes(task.type) &&
              task.activity_id &&
              activityDeadlines.has(task.activity_id)
            ) {
              return {
                ...task,
                activity_deadline: activityDeadlines.get(task.activity_id),
              };
            }

            return task;
          });

          return this.tasks;
        },
      );
    },

    async updateTask(
      taskId: string,
      payload:
        | ActivityUpdate
        | ActionUpdateRequest
        | CompletionCriterionUpdateRequest,
    ) {
      const task = this.tasks.find((task) => task.id === taskId);
      if (!task) {
        throw new Error('Task not found');
      }

      const { type, project: projectId, activity_id: activityId } = task;
      let result: ActivityDetails | ActionDetails | CompletionCriterionDetails;

      if (!projectId || !taskId) {
        throw new Error('Missing required task properties');
      }

      const projectStore = useProjectStore();

      switch (type) {
        case 'Activity': {
          result = await ProjectsService.updateProjectsActivity(
            projectId,
            taskId,
            payload as ActivityUpdate,
          );
          projectStore.writeActivitySummary(projectId, result);
          // Also update activityProjectStore if it has this activity loaded
          // (needed for date picker boundary reactivity)
          const activityProjectStore = useActivityProjectStore();
          if (activityProjectStore.hasActivity(projectId, taskId)) {
            activityProjectStore.write(projectId, result as ActivityDetails);
          }
          this.syncChildTaskDueDates(taskId, result as ActivityDetails);
          break;
        }

        case 'Action':
          if (!activityId) {
            throw new Error('Missing activity_id');
          }
          result = await ProjectsService.activityUpdateAction(
            projectId,
            activityId,
            taskId,
            payload as ActionUpdateRequest,
          );
          projectStore.writeAction(
            projectId,
            activityId,
            result as ActionDetails,
          );
          break;

        case 'CC':
          if (!activityId) {
            throw new Error('Missing activity_id');
          }
          result = await ProjectsService.activityUpdateCompletionCriterion(
            projectId,
            activityId,
            taskId,
            payload,
          );
          projectStore.writeCompletionCriterion(projectId, activityId, result);
          break;

        default:
          throw new Error(`Unsupported task type: ${type}`);
      }

      this.writeTask(taskId, {
        state: result.state,
        status: result.status,
        date: result.current_deadline,
      });

      return result;
    },

    async loadMyProfilePicture(hasImage: boolean, noCache = false) {
      if (this.image) {
        URL.revokeObjectURL(this.image);
      }
      if (!hasImage || !this.profile.id) {
        this.image = '';

        return;
      }
      this.image =
        (await fetchProfilePictureBlobUrl(this.profile.id, noCache).catch(
          () => null,
        )) ?? '';
    },

    async applyProfilePicture(hasImage: boolean, noCache = false) {
      await this.loadMyProfilePicture(hasImage, noCache);
      useUserStore().setUserAvatar(this.profile.id, this.image);
    },

    async uploadProfilePicture(file: File) {
      const settings = await UsersService.uploadProfilePicture({ file });
      Object.assign(this.$state.settings, settings);
      await this.applyProfilePicture(settings.has_profile_picture, true);
    },

    async deleteProfilePicture() {
      const settings = await UsersService.deleteProfilePicture();
      Object.assign(this.$state.settings, settings);
      await this.applyProfilePicture(false);
    },

    async syncMsPhoto(accessToken?: string) {
      const subdomain = getTenant();
      const tenantId = import.meta.env[`VITE_${subdomain}_TENANT_ID`];
      const appId = import.meta.env[`VITE_${subdomain}_CLIENT_ID`];
      const token = accessToken ?? (await entraGetTokenSilent(tenantId, appId));

      await UsersService.syncMsProfilePicture(token);
      this.$state.settings.has_profile_picture = true;
      this.$state.settings.profile_picture_is_custom = false;
      await this.applyProfilePicture(true, true);
    },

    incrementRequest() {
      return (this.openRequests += 1);
    },

    decrementRequest() {
      return (this.openRequests -= 1);
    },

    changePassword(password: string, token: string) {
      // TODO we have no token yet from the backend
      // so this is untested
      return UsersService.changeResetPassword({
        password,
        reset_password_token: token,
      });
    },

    syncChildTaskDueDates(activityId: string, activity: ActivityDetails) {
      const childTaskMap = new Map<string, string>();
      const activityDeadline = activity.current_deadline;

      for (const action of activity.actions) {
        childTaskMap.set(action.id, action.current_deadline);
      }
      for (const cc of activity.completion_criteria) {
        childTaskMap.set(cc.id, cc.current_deadline);
      }

      this.tasks.forEach((task, index) => {
        if (task.activity_id === activityId) {
          const updates: Partial<TaskWithActivityDeadline> = {};

          // Update child task's own deadline if changed
          if (task.id && childTaskMap.has(task.id)) {
            const newDate = childTaskMap.get(task.id)!;
            if (task.date !== newDate) {
              updates.date = newDate;
            }
          }

          // Always update activity_deadline on child tasks
          if (task.activity_deadline !== activityDeadline) {
            updates.activity_deadline = activityDeadline;
          }

          if (Object.keys(updates).length > 0) {
            this.tasks[index] = { ...task, ...updates };
          }
        }
      });
    },
  },
});
